import React, { useMemo, useState } from "react";

import { ButtonStandard, ModalBase, SingleSelectInput, Text, toast } from "@performyard/py-components";

import { useEmployeeFields } from "../../../api/hooks";

import { saveDirectoryColumns } from "../../../api/services/defs/directory-columns";

import type { DirectoryColumn } from "../DirectoryTab/rows";

/** Sentinel the legacy picker used for an empty slot; the field resolver drops it on read. */
const NONE = "none";

/** Display Name occupies slot one and is not configurable. */
const CONFIGURABLE_SLOT_COUNT = 4;

type ChangeColumnsModalProps = {
	isOpen: boolean;
	selectedColumns: DirectoryColumn[];
	onClose: () => void;
	/** Called after a successful save so the directory can pull the new column set. */
	onSaved: () => void;
};

const ChangeColumnsModal = ({
	isOpen,
	selectedColumns,
	onClose,
	onSaved,
}: ChangeColumnsModalProps): React.ReactElement => {
	const { employeeFields, isLoading } = useEmployeeFields();

	const [slots, setSlots] = useState<string[]>(() =>
		Array.from({ length: CONFIGURABLE_SLOT_COUNT }, (_unused, index) => selectedColumns[index]?._id ?? NONE)
	);
	const [isSaving, setIsSaving] = useState(false);

	const options = useMemo(() => {
		const employeeFieldOptions =
			(employeeFields ?? [])
				// Match the legacy Directory picker. The endpoint returns raw custom-field documents,
				// where `vis` is absent only when the ORM would default it to company-wide visibility.
				.filter((field) => field.name !== "Activation Status" && (field.vis ?? ["all"]).includes("all"))
				.map((field) => ({ label: field.name, value: String(field._id) })) ?? [];

		// Ensure all currently selected columns appear in options, even if employeeFields
		// hasn't loaded yet. This prevents raw IDs from showing on first modal open.
		const allFieldIds = new Set(employeeFieldOptions.map((opt) => opt.value));
		const missingSelectedOptions = selectedColumns
			.filter((col) => !allFieldIds.has(col._id))
			.map((col) => ({ label: col.name, value: col._id }));

		return [{ label: "None", value: NONE }, ...missingSelectedOptions, ...employeeFieldOptions];
	}, [employeeFields, selectedColumns]);

	const setSlot = (index: number, value: string | null) =>
		setSlots((previous) => previous.map((slot, slotIndex) => (slotIndex === index ? (value ?? NONE) : slot)));

	const handleSave = async () => {
		setIsSaving(true);
		try {
			// Display Name is sent as the leading slot the way the legacy picker did, so the
			// stored shape is unchanged for everything else that reads directory_fields.
			await saveDirectoryColumns(["displayname", ...slots]);
			onSaved();
			onClose();
		} catch {
			toast.error("There was an error saving the directory columns.");
			setIsSaving(false);
		}
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div
				className="flex w-[420px] max-w-[calc(100vw-32px)] flex-col gap-4 p-6"
				data-testid="change-columns-modal"
			>
				<div id="modal-content">
					<Text size="sub-heading" weight="semi-bold" color="neutral-9">
						Change Columns
					</Text>
				</div>
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					Choose which employee fields appear as columns in the directory.
				</Text>

				<div className="flex flex-col gap-3">
					<SingleSelectInput
						disabled
						ariaLabel="Display Name"
						options={[{ label: "Display Name", value: "displayname" }]}
						value="displayname"
						onChange={() => {}}
						data-testid="change-columns-slot-display-name"
					/>
					{slots.map((slot, index) => (
						<SingleSelectInput
							key={`directory-column-slot-${index}`}
							ariaLabel={`Directory column ${index + 2}`}
							selectionVariant="combobox"
							options={options}
							value={slot}
							disabled={isLoading}
							onChange={(value) => setSlot(index, value as string | null)}
							placeholder="Select a field..."
							data-testid={`change-columns-slot-${index + 1}`}
						/>
					))}
				</div>

				<div className="flex justify-end gap-2">
					<ButtonStandard variant="outline" color="neutral" onClick={onClose} disabled={isSaving}>
						Cancel
					</ButtonStandard>
					<ButtonStandard color="primary" onClick={handleSave} disabled={isSaving || isLoading}>
						Save
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};

export default ChangeColumnsModal;
